import Store from "../Redux/store";
import { getFilteredProducts } from "../utilities/ProductsFilter";
import { PRODUCTS_PAGE_SIZE } from "../utilities/productsConstants";
import { fetchProductsPage } from "../utilities/productsRefresh";

export const INVENTORY_CHANGED_EVENT = "inventory-changed";

let inventoryStale = false;

export function markInventoryStale() {
  inventoryStale = true;
}

export function consumeInventoryStale() {
  if (!inventoryStale) return false;
  inventoryStale = false;
  return true;
}

/** Flatten parent products and sellable variants for the inventory grid. */
export function flattenProductsForInventory(products) {
  if (!products || !Array.isArray(products)) return [];
  const inventory = [];
  products.forEach((product) => {
    if (!product) return;
    if (product?.varients?.length > 0) {
      product.varients
        .filter((item) => item?.isSellable)
        .forEach((variant) => {
          inventory.push({ ...variant, parentProduct: product.name });
        });
    } else {
      inventory.push(product);
    }
  });
  return inventory;
}

/** Merge fresh API quantities into the cached product list (including variants). */
export function mergeProductQuantities(existingProducts, freshProducts) {
  const qtyById = new Map();
  (freshProducts || []).forEach((p) => {
    const id = p?.POID || p?.id;
    if (id != null) qtyById.set(String(id), p.quantity);
    (p?.varients || []).forEach((v) => {
      const vid = v?.POID || v?.id;
      if (vid != null) qtyById.set(String(vid), v.quantity);
    });
  });

  if (!Array.isArray(existingProducts) || existingProducts.length === 0) {
    return freshProducts || [];
  }

  return existingProducts.map((p) => {
    if (!p) return p;
    const id = String(p.POID || p.id);
    let next = { ...p };
    if (qtyById.has(id)) next.quantity = qtyById.get(id);
    if (next.varients?.length) {
      next = {
        ...next,
        varients: next.varients.map((v) => {
          const vid = String(v?.POID || v?.id);
          return qtyById.has(vid) ? { ...v, quantity: qtyById.get(vid) } : v;
        }),
      };
    }
    return next;
  });
}

/**
 * Pull latest stock from POS (products + PiMS balance) and update Redux.
 * Inventory screen and product lists share these slices — never seed list from GET /inventory.
 */
export async function refreshProductInventoryInRedux(dispatch, { silent = true } = {}) {
  const data = await fetchProductsPage({
    start: 0,
    size: PRODUCTS_PAGE_SIZE,
    status: "Active",
    context: "Syncing inventory",
    silent,
  });

  if (!Array.isArray(data) || data.length === 0) {
    return false;
  }

  const current = Store.getState().productsState?.products || [];
  const merged = mergeProductQuantities(current, data);
  const activeProducts = getFilteredProducts(merged, "active");
  const activeForInventory = activeProducts.length > 0 ? activeProducts : data;

  const d = dispatch || Store.dispatch;
  d({ type: "GET_PRODUCTS", products: merged });
  d({ type: "GET_ACTIVE_PRODUCTS", active_products: activeForInventory });
  seedInventoryItemsFromProducts(activeForInventory, d);

  return true;
}

/**
 * Derive inventoryItems Redux slice from products (single source of truth for stock list UI).
 */
export function seedInventoryItemsFromProducts(products, dispatch) {
  const list = Array.isArray(products) ? products : [];
  const active = getFilteredProducts(list, "active");
  const source = active.length > 0 ? active : list;
  const d = dispatch || Store.dispatch;
  d({
    type: "GET_INVENTORY_ITEMS",
    inventoryItems: flattenProductsForInventory(source),
  });
}

const INVENTORY_NOTIFY_DEBOUNCE_MS = 400;
let inventoryNotifyTimer = null;
let inventoryRefreshInFlight = null;

/**
 * Subtract or add stock in Redux only (no API). Mirrors POS fulfill toggle.
 */
export function applyStockDeltaInRedux(dispatch, productIds, quantities, { add = false } = {}) {
  if (!productIds?.length || !quantities?.length) return;
  const ids = productIds.map(String);
  const qtys = quantities.map((q) => Number(q) || 0);
  const action = {
    type: "EDIT_ACTIVE_PRODUCTS_QUANTITY_ON_ORDER",
    id: ids,
    quantity: qtys,
    ...(add ? { add: true } : {}),
  };
  const d = dispatch || Store.dispatch;
  d(action);
  d({
    type: "EDIT_INVENTORY_ITEM_QUANTITY_ON_ORDER",
    id: ids,
    quantity: qtys,
    ...(add ? { add: true } : {}),
  });
  notifyInventoryChanged();
}

/** Apply stock deduction from delivery line items after DELIVERED (PiMS already updated on server).
 * Pass `{ add: true }` after CANCELLED when stock was previously deducted (server restocks).
 */
export function applyStockDeltaFromDeliveryLines(dispatch, items, { add = false } = {}) {
  const productIds = [];
  const quantities = [];
  (items || []).forEach((item) => {
    const pid = item?.productId ?? item?.productSpecificationId ?? item?.ProductSpecification_Id;
    const qty = Number(item?.deliveredQuantity ?? item?.deliveredQty ?? item?.quantity) || 0;
    if (pid != null && String(pid).trim() !== "" && qty > 0) {
      productIds.push(String(pid).trim());
      quantities.push(qty);
    }
  });
  if (productIds.length > 0) {
    applyStockDeltaInRedux(dispatch, productIds, quantities, { add });
  }
}

export function notifyInventoryChanged() {
  markInventoryStale();
  if (typeof window === "undefined") return;

  if (inventoryNotifyTimer) {
    clearTimeout(inventoryNotifyTimer);
  }
  inventoryNotifyTimer = setTimeout(() => {
    inventoryNotifyTimer = null;
    window.dispatchEvent(new CustomEvent(INVENTORY_CHANGED_EVENT));
  }, INVENTORY_NOTIFY_DEBOUNCE_MS);
}

/** Coalesce concurrent product/inventory refreshes into one API call. */
export async function runDebouncedInventoryRefresh(dispatch, options = {}) {
  if (inventoryRefreshInFlight) {
    return inventoryRefreshInFlight;
  }
  inventoryRefreshInFlight = refreshProductInventoryInRedux(dispatch, options).finally(() => {
    inventoryRefreshInFlight = null;
  });
  return inventoryRefreshInFlight;
}
